/**
 * Feature flags
 *
 * Use feature flags when you start working on feature which will not
 * be finished in one sprint, or the amount of work is divided in a few PR's.
 * After one of the PR's is merged, the code is pushed to the master branch, we
 * might build a new version and our partially finished feature will be visible to
 * the testers and others. We want to prevent that and we will flag that feature for all envs
 * except for development which is a dedicated env for our development process.
 *
 * Also we can exclude some of the features in other envs if there is a need to.
 * For example, we can push some features to master, but make them unavailable in
 * production because maybe the work is not finished or the feature is not yet
 * planned for a release.
 *
 * Usage:
 *
 * Add a unique flag name for a feature you wanna flag to type FeatureFlag
 *
 * e.g. type FeatureFlag = 'WORKOUTS'
 *
 * const flags: Record<string, FeatureFlag[]> = {
 *  development: [],
 *  uat: ['WORKOUTS'],
 *  production: ['WORKOUTS'],
 * }
 *
 * Component:
 *
 * {!isFeatureFlagged('WORKOUTS') && <WorkoutsComponent />}
 *
 * Or use the HOC:
 *
 * const flag = featureFlag('WORKOUTS')
 * const Workouts = flag((props: WorkoutsProps) => {
 *   return <div {...props}>Workouts component staff</div>
 * })
 *
 * And with this setup Workouts feature will be visible on dev, but not in any other env.
 *
 */

import { config } from './config'
import React from 'react'

type FeatureFlag = 'GROUPS' | 'STRAVA' | 'APPLE_SIGN_IN' | 'GOOGLE_SIGN_IN' | 'APP_TOUR'

const flags: Record<string, FeatureFlag[]> = {
  development: [],
  uat: [],
  production: ['GROUPS'],
}

export const isFeatureFlagged = (flag: FeatureFlag): boolean => flags[config.env]?.includes(flag) ?? false

export const featureFlag = (flag: FeatureFlag) => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  return <P extends {}>(Component: React.ComponentType<P>): React.FC<P> => {
    return (props: P): React.ReactElement => {
      return !isFeatureFlagged(flag) ? <Component {...props} /> : <></>
    }
  }
}
